import React, { useCallback, useEffect } from "react";

import {
    LoadingScreen,
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { RootStackScreenProps } from "../../../navigation";
import { AdminContainer } from "../../container";
import { Form } from "../form";
import { useHandleMutationError } from "../../../hooks";
import {
    GQVirtualPrinterSave,
    useVirtualPrinterLazyQuery,
    useVirtualPrinterSaveMutation,
} from "graphql-sdk";
import { schemaVirtualPrinter } from "lib";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

type ScreenProps = RootStackScreenProps<"VIRTUAL_PRINTER_EDIT">;

export function VirtualPrinterEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.printer");

    const [t] = useTranslation();
    const [virtualPrinterSave] = useVirtualPrinterSaveMutation();
    const [getVirtualPrinter, virtualPrinter] = useVirtualPrinterLazyQuery({
        fetchPolicy: "no-cache",
    });

    const form = useForm<GQVirtualPrinterSave>(schemaVirtualPrinter, null);
    const [{ values }, { setDefaultValues }] = form;
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();
    const virtualPrinterId = route.params.id;

    useEffect(() => {
        if (!virtualPrinterId) {
            navigate("VIRTUAL_PRINTERS");
            return;
        }

        getVirtualPrinter({ variables: { id: virtualPrinterId } });
    }, [getVirtualPrinter, navigate, virtualPrinterId]);

    useEffect(() => {
        if (virtualPrinter.data && virtualPrinter.data.virtualPrinter) {
            const virtualPrinterData = virtualPrinter.data.virtualPrinter;
            setDefaultValues({
                name: virtualPrinterData.name || "",
                printerId: virtualPrinterData?.printer?.id,
                departments: virtualPrinterData?.departments?.map(itr => {
                    return {
                        departmentId: itr?.department?.id,
                        printerId: itr?.printer?.id,
                    };
                }),
            });
        }
    }, [virtualPrinter.data, setDefaultValues, virtualPrinter]);

    const handleEdit = useCallback(async () => {
        if (
            !virtualPrinter.data ||
            !virtualPrinter.data.virtualPrinter ||
            !values
        ) {
            return;
        }

        await handleMutationError(
            async () =>
                await virtualPrinterSave({
                    variables: {
                        id: virtualPrinter.data!.virtualPrinter.id,
                        virtualPrinter: {
                            name: values.name,
                            printerId:
                                values.printerId !== ""
                                    ? values.printerId
                                    : undefined,
                            departments: values.departments,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                navigate("VIRTUAL_PRINTERS", { refetch: true });
            }
        );
    }, [
        virtualPrinter.data,
        values,
        handleMutationError,
        t,
        virtualPrinterSave,
        navigate,
    ]);

    if (!virtualPrinter.data || !virtualPrinter.data.virtualPrinter) {
        return <LoadingScreen style="light" />;
    }

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleEdit}
                        submitButton={["common.save", "Save"]}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
